<template>
  <application-layout>
    <v-main>
      <v-container>
        <h1 class="mb-4">Manage Accountability Assistant Settings</h1>
        <v-tabs show-arrows>
          <v-tab
            data-testid="manage-score-thresholds-tab"
            class="manage-score-thresholds-tab"
          >
            Manage Score Thresholds
          </v-tab>
          <v-tab
            data-testid="manage-call-thresholds-tab"
            class="manage-call-thresholds-tab"
          >
            Manage Call Thresholds
          </v-tab>

          <v-tab-item
            :style="darkTheme ? darkThemeBgColor : ''"
            v-if="scoreThreshold.length"
          >
            <v-form
              ref="scoreThresholdsForm"
              @submit.prevent="submitScoreThresholds"
            >
              <h2 class="mt-4 mb-2">Manage Score Thresholds</h2>
              <v-btn
                data-testid="add-score-thresholds-btn"
                class="add-score-thresholds-btn mb-4 mx-0"
                color="primary"
                @click="addScoreThreshold"
              >
                <v-icon>add</v-icon>
                Add Score Threshold
              </v-btn>
              <transition-group
                name="scroll-x-transition"
                tag="div"
              >
                <v-row
                  class="mb-3"
                  justify="space-between"
                  align="center"
                  v-for="(threshold, idx) in scoreThreshold"
                  :key="threshold.color"
                >
                  <v-col cols="3">
                    <app-input
                      data-testid="minimum-score-input"
                      class="minimum-score-input"
                      :type="'text'"
                      label="Minimum Score"
                      v-model="threshold.threshold"
                      :rules="{ required: true, numeric: true }"
                      :useValidation="true"
                    />
                  </v-col>
                  <v-col
                    cols="3"
                    align-self="center"
                    class="ml-5"
                  >
                    <div class="text-center">
                      <span>Threshold color:&nbsp;</span>
                      <span
                        :style="`color: ${thresholdColor(threshold.color)}`"
                        v-if="threshold.color"
                      >
                        {{ thresholdColor(threshold.color) }}
                      </span>
                    </div>
                  </v-col>
                  <v-col cols="2">
                    <v-btn
                      data-testid="select-color-btn"
                      class="select-color-btn"
                      color="primary"
                      block
                      @click="openColorPicker(threshold, idx)"
                    >
                      Select Color
                    </v-btn>
                  </v-col>
                  <v-col cols="3" class="text-center">
                    <v-btn
                      data-testid="delete-score-threshold-btn"
                      class="delete-score-threshold-btn"
                      fab
                      small
                      color="error"
                      @click="openDelete('score threshold', idx)"
                    >
                      <v-icon>close</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </transition-group>
              <div>
                <v-btn
                  data-testid="save-score-threshold-btn"
                  class="save-score-threshold-btn ml-0"
                  type="submit"
                  color="primary"
                  :loading="loading"
                  :disabled="loading"
                >
                  Save Score Thresholds
                </v-btn>
              </div>
            </v-form>
          </v-tab-item>

          <v-tab-item :style="darkTheme ? darkThemeBgColor : ''">
            <v-form
              ref="callThresholdsForm"
              @submit.prevent="submitCallThresholds"
            >
              <h2 class="mt-4 mb-2">Manage Call Thresholds</h2>
              <v-btn
                data-testid="add-call-thresholds-btn"
                class="add-call-thresholds-btn mb-4 mx-0"
                @click="addCallThreshold"
                color="primary"
              >
                <v-icon>add</v-icon>
                Add Call Threshold
              </v-btn>
              <transition-group name="scroll-x-transition" tag="div">
                <v-row
                  v-for="(threshold, i) in callThreshold"
                  :key="threshold.label"
                  class="my-2"
                >
                  <v-col>
                    <span class="subheading font-weight-bold">{{ threshold.label }}</span>
                    <v-row justify="space-between">
                      <v-col cols="3">
                        <app-input
                          data-testid="unique-calls-input"
                          class="unique-calls-input"
                          :type="'text'"
                          label="Unique Calls"
                          v-model="threshold.calls"
                          :rules="{ required: true }"
                          :useValidation="true"
                        />
                      </v-col>
                      <v-col cols="3">
                        <app-input
                          data-testid="min-days-past-input"
                          class="min-days-past-input"
                          :type="'text'"
                          label="Min Days After The Start Of The Month"
                          v-model="threshold.min_threshold"
                          :rules="{ required: true, numeric: true }"
                          :useValidation="true"
                        />
                      </v-col>
                      <v-col cols="3">
                        <app-input
                          data-testid="max-days-past-input"
                          class="max-days-past-input"
                          :type="'text'"
                          label="Max Days After The Start Of The Month"
                          v-model="threshold.max_threshold"
                          :rules="{ required: true, numeric: true }"
                          :useValidation="true"
                        />
                      </v-col>
                      <v-col cols="2" class="text-center">
                        <v-btn
                          data-testid="delete-call-threshold-btn"
                          class="delete-call-threshold-btn"
                          fab
                          small
                          color="error"
                          @click="openDelete('call threshold', i)"
                        >
                          <v-icon>close</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </transition-group>
              <div>
                <v-btn
                  data-testid="save-call-threshold-btn"
                  type="submit"
                  color="primary"
                  class="save-call-threshold-btn ml-0 mb-12"
                  :loading="loading"
                  :disabled="loading"
                >
                  Save Call Thresholds
                </v-btn>
              </div>
            </v-form>
          </v-tab-item>
        </v-tabs>
      </v-container>
      <v-snackbar
        color="error"
        right
        v-model="error"
      >
        {{ errorMessage }}
      </v-snackbar>

      <v-snackbar
        color="success"
        right
        v-model="success"
      >
        {{ successMessage }}
      </v-snackbar>
    </v-main>

    <delete-modal
      v-model="modal"
      @delete="deleteItem"
      :itemType="itemType"
      :advisory="advisory"
    />

    <v-dialog
      v-model="colorPicker"
      width="18.75rem"
    >
      <v-card>
        <v-card-title
          class="headline"
          primary-title
        >
          Select Color
        </v-card-title>
          <v-color-picker
            v-if="selectedThreshold"
            v-model="selectedThreshold.color"
            class="mt-2"
            show-swatches
          />
          <v-divider class="white" />
          <v-card-actions>
          <v-btn
            data-testid="cancel-select-color-btn"
            class="cancel-select-color-btn"
            color="error"
            @click="colorPicker = false"
          >
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn
            data-testid="set-select-color-btn"
            class="set-select-color-btn"
            color="primary"
            @click="setColor(selectedThreshold.index)"
          >
            Select
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </application-layout>
</template>

<script>
import AppInput from "@/components/global/AppInput";
import ApplicationLayout from "@/layouts/ApplicationLayout.vue";
import DeleteModal from "@/components/DeleteModal.vue";
import { advisoryRestorePreviousSession } from "@/helpers/warning-advisory-text";

export default {
  data() {
    return {
      advisory: advisoryRestorePreviousSession,
      loading: false,
      modal: false,
      colorPicker: false,
      itemType: "",
      selectedItemIndex: "",
      selectedThreshold: "",
      error: false,
      errorMessage: null,
      success: false,
      successMessage: null,
      scoreThreshold: [],
      callThreshold: []
    };
  },

  methods: {
    addScoreThreshold() {
      this.scoreThreshold.push({
        threshold: 0,
        color: "#194d33"
      });
    },

    addCallThreshold() {
      this.callThreshold.push({
        label: `Leg ${this.callThreshold.length + 1}`,
        calls: 0,
        max_threshold: 0,
        min_threshold: 0
      });
    },

    openDelete(itemType, selectedItemIndex) {
      this.modal = true;
      this.itemType = itemType;
      this.selectedItemIndex = selectedItemIndex;
    },

    openColorPicker(threshold, i) {
      this.colorPicker = true;
      this.selectedThreshold = threshold;
    },

    deleteScoreThreshold(index) {
      this.scoreThreshold.splice(index, 1);
    },

    deleteCallThreshold(index) {
      this.callThreshold.splice(index, 1);
    },

    deleteItem() {
      if (this.itemType === "score threshold") {
        this.deleteScoreThreshold(this.selectedItemIndex);
      } else if (this.itemType === "call threshold") {
        this.deleteCallThreshold(this.selectedItemIndex);
      };
      this.modal = false;
      this.selectedItemIndex = "";
      this.itemType = "";
    },

    setColor(index) {
      this.selectedThreshold = "";
      this.colorPicker = false;
    },

    thresholdColor(color) {
      return typeof color === "object" ? color.hex : color;
    },

    sortThreshold(threshold) {
      return threshold.sort((a, b) => a.threshold - b.threshold);
    },

    submitScoreThresholds() {
      this.loading = true;

      const payload = {
        ident: "global_accountability_color",
        value: JSON.stringify(this.sortThreshold(this.scoreThreshold))
      };

      this.$store.dispatch("updateGlobalSetting", payload)
        .then(() => {
          this.success = true;
          this.successMessage = "Your changes have been saved.";

          this.loading = false;
        })
        .catch(err => {
          this.errorMessage = "An error occurred.";

          if (err.response && err.response.data && err.response.data.message) {
            this.errorMessage = err.response.data.message;
          }

          this.error = true;
          this.loading = false;
        });
    },

    submitCallThresholds() {
      this.loading = true;

      const payload = {
        ident: "global_accountability_call_rules",
        value: JSON.stringify(this.callThreshold)
      };

      this.$store.dispatch("updateGlobalSetting", payload)
        .then(() => {
          this.success = true;
          this.successMessage = "Your changes have been saved.";

          this.loading = false;
        })
        .catch(err => {
          this.errorMessage = "An error occurred.";

          if (err.response && err.response.data && err.response.data.message) {
            this.errorMessage = err.response.data.message;
          }

          this.error = true;
          this.loading = false;
        });
    }
  },

  computed: {
    darkThemeBgColor() {
      return this.$store.state.app.darkThemeBgColor;
    },
    largeGlobalSettings: {
      get() {
        return this.$store.state.globalSetting.largeGlobalSettings;
      },
      set(newValue) {
        this.$store.commit("setLargeGlobalSettingsData", newValue);
      }
    },

    darkTheme() {
      return this.$vuetify.theme.dark;
    }
  },

  created() {
    this.loading = true;

    this.$store.dispatch("getAllGlobalSettings")
      .then(resp => {
        this.scoreThreshold = JSON.parse(resp.data.large_global_settings.find(setting => setting.ident === "global_accountability_color").value);
        this.callThreshold = JSON.parse(resp.data.large_global_settings.find(setting => setting.ident === "global_accountability_call_rules").value);

        this.loading = false;
      });
  },

  components: {
    ApplicationLayout,
    AppInput,
    DeleteModal
  }
};
</script>
